import isEqual from 'lodash.isequal';
import { call, takeEvery } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';

import {
  trackBrandKitChanges,
  brandKitColorUsed,
  brandKitStyleUsed,
  BrandKitViewLeftPayload
} from 'js/actionCreators/brandKitTrackingActions';

import { track } from '../mixpanelProvider';

function* handleBrandKitViewLeft({ payload }: PayloadAction<BrandKitViewLeftPayload>) {
  const { colorsBefore, colorsAfter, fontsBefore, fontsAfter, overlayBefore, overlayAfter } = payload;

  if (!isEqual(colorsBefore, colorsAfter)) {
    yield call(track, 'Brand Kit Colours Updated', {
      'Number of Colours': colorsAfter.length
    });
  }

  if (!isEqual(fontsBefore, fontsAfter)) {
    const beforeStyles = fontsBefore.styles;
    const afterStyles = fontsAfter.styles;
    const minLen = Math.min(beforeStyles.length, afterStyles.length);

    yield call(track, 'Brand Kit Font & Text Settings Updated', {
      'Use Brand Kit Fonts Changed': fontsBefore.useBrandKitFonts !== fontsAfter.useBrandKitFonts,
      'Font Family Changed': beforeStyles.slice(0, minLen).some((s, i) => !isEqual(s.font, afterStyles[i].font)),
      'Color Changed': beforeStyles.slice(0, minLen).some((s, i) => s.colorHex !== afterStyles[i].colorHex),
      'Line Height Changed': beforeStyles.slice(0, minLen).some((s, i) => s.lineSpacing !== afterStyles[i].lineSpacing),
      'Font Size Changed': beforeStyles.slice(0, minLen).some((s, i) => s.fontSize !== afterStyles[i].fontSize),
      'Bold Changed': beforeStyles.slice(0, minLen).some((s, i) => s.bold !== afterStyles[i].bold),
      'Italic Changed': beforeStyles.slice(0, minLen).some((s, i) => s.italic !== afterStyles[i].italic),
      'Align Changed': beforeStyles.slice(0, minLen).some((s, i) => s.align !== afterStyles[i].align),
      'Number of Styles': afterStyles.length
    });
  }

  if (!isEqual(overlayBefore, overlayAfter)) {
    yield call(track, 'Brand Kit Overlay Settings Updated', {
      Position: overlayAfter.position,
      Opacity: overlayAfter.opacity,
      Size: overlayAfter.size,
      'Show Logo': overlayAfter.showOverlay
    });
  }
}

function* handleBrandKitColorUsed({ payload }: PayloadAction<{ colorName: string }>) {
  yield call(track, 'Brand Kit Color Used', { 'Color name': payload.colorName });
}

function* handleBrandKitStyleUsed({ payload }: PayloadAction<{ styleName: string }>) {
  yield call(track, 'Brand Kit Style Used', { 'Style name': payload.styleName });
}

export default function* trackBrandKitSaga() {
  yield takeEvery(trackBrandKitChanges.type, handleBrandKitViewLeft);
  yield takeEvery(brandKitColorUsed.type, handleBrandKitColorUsed);
  yield takeEvery(brandKitStyleUsed.type, handleBrandKitStyleUsed);
}
